import React from "react";
import { Trans } from "@lingui/react/macro";
import { msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import type { I18n } from "@lingui/core";
import { formatDateDisplay } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info, TrendingUp, AlertTriangle } from "lucide-react";
import { formatCHF, rappenToCHF, DEFAULT_CURRENCY } from "@/lib/money";
import type {
  EnhancedMultiGroupCostResult,
  PeriodCostBreakdown,
  DiscountsData,
} from "@/types/billing";
import type { CustomEnum } from "@/types/organisation";
import type { settings_ServiceResponse } from "@/api/models/settings_ServiceResponse";
import { useCurrency } from "@/hooks/useCurrency";

// Helper function to get translated age type labels
function getAgeTypeLabel(ageType: string, i18n: I18n): string {
  const ageTypeMap: Record<string, string> = {
    BABY: i18n._({ id: "tariff.ageTypes.baby", message: "Baby" }),
    CHILD: i18n._({ id: "tariff.ageTypes.child", message: "Child" }),
    TODDLER: i18n._({ id: "tariff.ageTypes.toddler", message: "Toddler" }),
  };
  return ageTypeMap[ageType] || ageType;
}

/**
 * The localized-name columns on the generated service model: name_de … name_pt. Narrowing the
 * computed key to this union turns `service[key]` into a checked property access rather than an
 * implicit-any index (#1153); an unmapped locale still falls back to `service.name` below.
 */
type LocalizedServiceNameKey = Extract<keyof settings_ServiceResponse, `name_${string}`>;

// Helper function to get translated service label from services config
function getServiceLabel(code: string, services: settings_ServiceResponse[], i18n: I18n): string {
  const service = services.find((s) => s.code === code);
  if (service) {
    // Use localized name based on current locale
    const localeKey = `name_${i18n.locale}` as LocalizedServiceNameKey;
    const localizedName = service[localeKey] || service.name;
    return localizedName || code;
  }
  return code;
}

// Helper function to get translated group label from custom enums
function getGroupLabel(group: string, groups: CustomEnum[], i18n: I18n): string {
  // First try to find in custom enums
  const groupEnum = groups.find((g) => g.enum_value === group);
  if (groupEnum) {
    // Use translation for the current locale
    const locale = i18n.locale || 'en';
    const translation = groupEnum.label_translations?.[locale];
    if (translation) return translation;
    if (groupEnum.label_default) return groupEnum.label_default;
  }

  // Fallback to hardcoded translations for common groups
  const groupLabels: Record<string, string> = {
    STANDARD: i18n._({ id: "tariff.groups.standard", message: "Standard" }),
    VIP: i18n._({ id: "tariff.groups.vip", message: "VIP" }),
    PREMIUM: i18n._({ id: "tariff.groups.premium", message: "Premium" }),
  };

  return groupLabels[group] || group;
}

interface EnhancedContractSummaryProps {
  costResult: EnhancedMultiGroupCostResult;
  discounts?: DiscountsData;
  subsidised?: boolean;
  subsidyAmount?: number; // in Rappen
  className?: string;
  hasWeekBasedServices?: boolean; // Pass this from parent if week factor services are detected
  services?: settings_ServiceResponse[]; // Service configuration from billing settings
  groups?: CustomEnum[]; // Custom enum groups
  isEditMode?: boolean; // Whether in edit mode (for layout adjustments)
}

export function EnhancedContractSummary({
  costResult,
  discounts,
  subsidised,
  subsidyAmount,
  className,
  hasWeekBasedServices: hasWeekBasedServicesProp,
  services = [],
  groups = [],
  isEditMode = false,
}: EnhancedContractSummaryProps) {
  const { totalCostBreakdown, warnings = [] } =
    costResult || ({} as EnhancedMultiGroupCostResult);
  const {
    periods = [],
    hasTransition: _hasTransition = false,
    transitionDate: _transitionDate,
  } = totalCostBreakdown ||
  ({} as EnhancedMultiGroupCostResult["totalCostBreakdown"]);
  const { currency } = useCurrency();
  const activeCurrency = currency || DEFAULT_CURRENCY;
  const formatAmount = React.useCallback(
    (valueInRappen: number) =>
      formatCHF(rappenToCHF(valueInRappen), true, activeCurrency),
    [activeCurrency]
  );

  // Calculate final amount (keeping for potential future use)
  // const _finalAmount = totalAmount - totalDiscountAmount;

  // Calculate estimated contract total (sum of all periods)
  const estimatedContractTotal = React.useMemo(() => {
    if (!periods || periods.length === 0) return 0;

    const countInclusiveMonths = (startISO: string, endISO: string): number => {
      const s = new Date(startISO);
      const e = new Date(endISO);
      if (!Number.isFinite(s.getTime()) || !Number.isFinite(e.getTime())) return 0;
      return (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()) + 1;
    };

    return periods.reduce((sum, period, _idx) => {
      // Recalculate monthly total per period (mirror EnhancedPeriodCard logic)
      const subsidyAmt = (subsidised && subsidyAmount != null) ? subsidyAmount : 0;
      const weekdayDiscountAmount = discounts?.weekday
        ? Object.values(discounts.weekday).reduce((acc, v) => acc + v, 0)
        : 0;
      const customDiscountAmount = discounts?.custom
        ? discounts.custom.reduce((acc, d) => {
            if (d.type === 'amount') return acc + d.value;
            return acc + Math.round((period.monthly * d.value) / 100);
          }, 0)
        : 0;
      const monthlyTotal = period.monthly - subsidyAmt - (weekdayDiscountAmount + customDiscountAmount);
      const months = countInclusiveMonths(period.startDate, period.endDate);
      return sum + Math.max(0, monthlyTotal) * Math.max(0, months);
    }, 0);
  }, [periods, discounts, subsidised, subsidyAmount]);

  // Check if there are any week-based services
  // If not provided via props, detect from periods data
  const hasWeekBasedServices = React.useMemo(() => {
    if (hasWeekBasedServicesProp !== undefined) {
      return hasWeekBasedServicesProp;
    }
    // Check if any services in the periods have week-based patterns
    // This could be expanded to detect patterns like "3-4 days per week" etc.
    return periods.some(period => {
      if (!period.daily) return false;
      const dailyKeys = Object.keys(period.daily);
      // If services are not consistent across all weekdays, might indicate week-based billing
      return dailyKeys.length > 0 && dailyKeys.length < 5;
    });
  }, [hasWeekBasedServicesProp, periods]);

  return (
    <div className={className}>
      {/* Main Cost Summary Card matching the story mockup */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            <Trans id="contracts.wizard.summary.costSummary">
              Cost Summary
            </Trans>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Period breakdown cards - side by side in edit mode when 2 periods */}
          {isEditMode && periods.length === 2 ? (
            <div className="grid grid-cols-2 gap-4">
              {periods.map((period, index) => (
                <EnhancedPeriodCard
                  key={index}
                  period={period}
                  periodNumber={index + 1}
                  discounts={discounts}
                  subsidised={subsidised}
                  subsidyAmount={subsidyAmount}
                  formatAmount={formatAmount}
                  services={services}
                  groups={groups}
                />
              ))}
            </div>
          ) : (
            periods.map((period, index) => (
              <EnhancedPeriodCard
                key={index}
                period={period}
                periodNumber={index + 1}
                discounts={discounts}
                subsidised={subsidised}
                subsidyAmount={subsidyAmount}
                formatAmount={formatAmount}
                services={services}
                groups={groups}
              />
            ))
          )}

          {/* Estimated Contract Total */}
          <div className="mt-6 pt-4 border-t">
            <div className="flex justify-between items-center">
              <span className="text-base font-semibold">
                <Trans id="contracts.wizard.summary.estimatedTotal">
                  Estimated Contract Total:
                </Trans>
              </span>
              <span className="text-lg font-bold text-primary">
                {formatAmount(estimatedContractTotal)}
              </span>
            </div>
          </div>

          {/* Week-based services warning */}
          {hasWeekBasedServices && (
            <Alert className="mt-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <Trans id="contracts.wizard.summary.weekBasedWarning">
                  Week-based services: amounts may vary by month
                </Trans>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Additional warnings */}
      {warnings && warnings.length > 0 && (
        <Card className="mt-4">
          <CardContent className="pt-6">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-warning-on-surface mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-warning-on-surface">
                  <Trans id="contracts.wizard.summary.warnings">
                    Please review the following:
                  </Trans>
                </p>
                <ul className="text-sm text-warning-on-surface space-y-1">
                  {warnings.map((warning, index) => (
                    <li key={index}>• {warning}</li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Enhanced period card matching story mockup
interface EnhancedPeriodCardProps {
  period: PeriodCostBreakdown;
  periodNumber: number;
  discounts?: DiscountsData;
  subsidised?: boolean;
  subsidyAmount?: number; // in Rappen
  formatAmount: (valueInRappen: number) => string;
  services?: settings_ServiceResponse[];
  groups?: CustomEnum[];
}

function EnhancedPeriodCard({
  period,
  periodNumber,
  discounts,
  subsidised,
  subsidyAmount,
  formatAmount,
  services = [],
  groups = [],
}: EnhancedPeriodCardProps) {
  const { i18n } = useLingui();

  // Calculate discount amounts for this period
  const weekdayDiscountAmount = React.useMemo(() => {
    if (!discounts?.weekday) return 0;
    return Object.values(discounts.weekday).reduce(
      (sum, amount) => sum + amount,
      0
    );
  }, [discounts?.weekday]);

  const customDiscountAmount = React.useMemo(() => {
    if (!discounts?.custom) return 0;
    return discounts.custom.reduce((sum, discount) => {
      if (discount.type === "amount") {
        return sum + discount.value;
      } else if (discount.type === "percentage") {
        return sum + (period.monthly * discount.value) / 100;
      }
      return sum;
    }, 0);
  }, [discounts?.custom, period.monthly]);

  const baseMonthly = period.monthly;
  const subsidyAmt = (subsidised && subsidyAmount != null) ? subsidyAmount : 0;
  const totalDiscounts = weekdayDiscountAmount + customDiscountAmount;
  const monthlyTotal = baseMonthly - subsidyAmt - totalDiscounts;

  // Format dates
  const startDate = formatDateDisplay(new Date(period.startDate));
  const endDate = formatDateDisplay(new Date(period.endDate));

  // Calculate months for display (inclusive months between start and end)
  const months = React.useMemo(() => {
    const s = new Date(period.startDate);
    const e = new Date(period.endDate);
    if (!Number.isFinite(s.getTime()) || !Number.isFinite(e.getTime())) return 0;
    return (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()) + 1;
  }, [period.startDate, period.endDate]);

  return (
    <div className="border rounded-lg p-4 space-y-3">
      {/* Period Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">
            <Trans id="contracts.wizard.summary.periodTitle">
              Period {periodNumber}: {getAgeTypeLabel(period.ageType, i18n)} Rate
            </Trans>
          </span>
        </div>
      </div>

      {/* Date Range */}
      <div className="text-sm">
        {startDate} - {endDate} ({months} {months === 1 ? "month" : "months"})
      </div>

  {/* Removed age transition banner per request */}

      {/* Service Pattern */}
      <div className="space-y-1">
        <div className="text-sm font-medium">
          <Trans id="contracts.wizard.summary.servicePattern">
            Service Pattern:
          </Trans>
        </div>
        <div className="text-sm space-y-1">
          {period.daily && Object.keys(period.daily).length > 0 ? (
            <ServicePatternDisplay daily={period.daily} i18n={i18n} services={services} groups={groups} />
          ) : (
            <div className="text-sm italic text-muted-foreground">
              <Trans id="contracts.wizard.summary.noServices">
                No services configured
              </Trans>
            </div>
          )}
        </div>
      </div>

      {/* Cost Breakdown */}
      <div className="space-y-2 pt-2 border-t">
        {/* Base Monthly */}
        <div className="flex justify-between items-center">
          <span className="text-sm">
            <Trans id="contracts.wizard.summary.baseMonthly">
              Base Monthly:
            </Trans>
          </span>
          <span className="text-sm font-medium">
            {formatAmount(baseMonthly)}
          </span>
        </div>

        {/* Subsidy Amount */}
        {subsidyAmt > 0 && (
          <div className="flex justify-between items-center">
            <span className="text-sm">
              <Trans id="contracts.wizard.review.subsidyAmount">
                Subsidy Amount:
              </Trans>
            </span>
            <span className="text-sm font-medium text-success-on-surface">
              -{formatAmount(subsidyAmt)}
            </span>
          </div>
        )}

        {/* Weekday Discounts */}
        {weekdayDiscountAmount > 0 && (
          <div className="flex justify-between items-center">
            <span className="text-sm">
              <Trans id="contracts.wizard.summary.weekdayDisc">
                Weekday Disc:{" "}
                {discounts?.weekday &&
                  Object.keys(discounts.weekday)
                    .map((day) => getWeekdayName(parseInt(day), i18n))
                    .join(", ")}
              </Trans>
            </span>
            <span className="text-sm font-medium text-success-on-surface">
              -{formatAmount(weekdayDiscountAmount)}
            </span>
          </div>
        )}

        {/* Custom Discounts - show each by entered name */}
        {discounts?.custom && discounts.custom.length > 0 && (
          <div className="space-y-1">
            {discounts.custom.map((discount, idx) => {
              const amount =
                discount.type === "percentage"
                  ? Math.round((baseMonthly * discount.value) / 100)
                  : discount.value;

              return (
                <div
                  key={discount.id || idx}
                  className="flex justify-between items-center"
                >
                  <span className="text-sm">
                    {discount.name || i18n._(msg`Discount ${idx + 1}`)}
                    {discount.type === "percentage" ? ` (${discount.value}%)` : ""}
                  </span>
                  <span className="text-sm font-medium text-success-on-surface">
                    -{formatAmount(amount)}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Separator */}
        <div className="border-t my-2"></div>

        {/* Monthly Total */}
        <div className="flex justify-between items-center">
          <span className="text-sm font-semibold">
            <Trans id="contracts.wizard.summary.monthlyTotal">
              Monthly Total:
            </Trans>
          </span>
          <span className="text-sm font-bold">
            {formatAmount(monthlyTotal)}
          </span>
        </div>
      </div>
    </div>
  );
}

// Helper function to get weekday names
function getWeekdayName(day: number, i18n: I18n): string {
  const weekdays = {
    1: i18n._({ id: "common.days.monday", message: "Monday" }),
    2: i18n._({ id: "common.days.tuesday", message: "Tuesday" }),
    3: i18n._({ id: "common.days.wednesday", message: "Wednesday" }),
    4: i18n._({ id: "common.days.thursday", message: "Thursday" }),
    5: i18n._({ id: "common.days.friday", message: "Friday" }),
  };
  return weekdays[day as keyof typeof weekdays] || `Day ${day}`;
}

// Component to display service patterns from daily data
interface ServicePatternDisplayProps {
  daily: Record<string, {
    services: string[];
    amount: number;
    details?: Array<{group: string; services: string[]}>
  }>;
  i18n: I18n;
  services?: settings_ServiceResponse[];
  groups?: CustomEnum[];
}

function ServicePatternDisplay({ daily, i18n, services = [], groups = [] }: ServicePatternDisplayProps) {
  // Process the daily data to group services by pattern
  const servicePatterns = React.useMemo(() => {
    const patterns: Map<string, { group: string; services: Set<string>; days: Set<number> }> = new Map();

    // Process each day's services
    const dayNameToKey: Record<string, number> = {
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
      sunday: 7,
    };

    Object.entries(daily).forEach(([dayStr, dayData]) => {
      let day = Number.parseInt(dayStr, 10);
      if (!Number.isFinite(day)) {
        const mapped = dayNameToKey[dayStr.toLowerCase() as keyof typeof dayNameToKey];
        day = mapped ?? NaN;
      }

      if (dayData.details && dayData.details.length > 0) {
        // Use detailed group information if available
        dayData.details.forEach(detail => {
          const key = `${detail.group}:${detail.services.join(',')}`;
          if (!patterns.has(key)) {
            patterns.set(key, {
              group: detail.group,
              services: new Set(detail.services),
              days: new Set()
            });
          }
          patterns.get(key)!.days.add(day);
        });
      } else if (dayData.services && dayData.services.length > 0) {
        // Fall back to simple services list
        const key = dayData.services.join(',');
        if (!patterns.has(key)) {
          patterns.set(key, {
            group: 'STANDARD', // Default group name
            services: new Set(dayData.services),
            days: new Set()
          });
        }
        patterns.get(key)!.days.add(day);
      }
    });

    return Array.from(patterns.values());
  }, [daily]);

  // Format day ranges for display
  const formatDays = (days: Set<number>) => {
    const sortedDays = Array.from(days).sort((a, b) => a - b);
    const dayNames = sortedDays.map(d => getWeekdayShortName(d, i18n));

    // Check for consecutive weekdays
    if (sortedDays.length === 5 && sortedDays[0] === 1 && sortedDays[4] === 5) {
      return i18n._({ id: "common.days.weekdays", message: "Mon-Fri" });
    }

    // Format as list of days
    if (dayNames.length <= 3) {
      return dayNames.join(', ');
    }

    // Format as range if consecutive
    const isConsecutive = sortedDays.every((day, idx) =>
      idx === 0 || day === sortedDays[idx - 1] + 1
    );

    if (isConsecutive) {
      return `${dayNames[0]}-${dayNames[dayNames.length - 1]}`;
    }

    return dayNames.join(', ');
  };

  if (servicePatterns.length === 0) {
    return (
      <div className="text-sm italic text-muted-foreground">
        <Trans id="contracts.wizard.summary.noServices">
          No services configured
        </Trans>
      </div>
    );
  }

  return (
    <>
      {servicePatterns.map((pattern, index) => (
        <div key={index}>
          • <span className="font-medium">{getGroupLabel(pattern.group, groups, i18n)}:</span>{' '}
          {Array.from(pattern.services).map(service => getServiceLabel(service, services, i18n)).join(', ')} on {formatDays(pattern.days)}
        </div>
      ))}
    </>
  );
}

// Helper function to get short weekday names
function getWeekdayShortName(day: number, i18n: I18n): string {
  const weekdays = {
    1: i18n._({ id: "common.days.short.monday", message: "Mon" }),
    2: i18n._({ id: "common.days.short.tuesday", message: "Tue" }),
    3: i18n._({ id: "common.days.short.wednesday", message: "Wed" }),
    4: i18n._({ id: "common.days.short.thursday", message: "Thu" }),
    5: i18n._({ id: "common.days.short.friday", message: "Fri" }),
  };
  return weekdays[day as keyof typeof weekdays] || `D${day}`;
}
